import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminUserTabs from "@/components/AdminUserTabs";
import { toast } from "sonner";
import {
  Users, ArrowLeft, ShieldCheck, Loader2, ChevronRight,
  Coins, TrendingUp, Mail, Crown, MessageCircle, Download,
  Send, X as XIcon, CheckSquare, Square,
} from "lucide-react";

/**
 * Admin-only dashboard opened when an admin taps a Top Referrer row.
 * Shows every signup this user brought in, with click-through profile
 * links so the admin can jump into each downline member's account.
 * Regular users see a redirect banner.
 */
function fmtUsd(cents) { return `$${((Number(cents) || 0) / 100).toFixed(2)}`; }
function fmtNum(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return v.toString();
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

// Dependency-free CSV download — same helper as UserShareAdmin.
function downloadCsv(filename, headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(",")];
  for (const r of rows) lines.push(r.map(esc).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function UserSignupsAdmin() {
  const { user, loading: authLoading } = useAuth();
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Multi-select for the bulk-DM flow. Stores usernames (lowercased)
  // because that's what the backend accepts and dedupes on.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [bulkCoins, setBulkCoins] = useState(0);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!username) return;
    // Wait for auth to settle before firing the admin request — otherwise
    // the request may leave without a Bearer token attached and 401 out,
    // which we surface as a misleading "admin-only" gate.
    if (authLoading) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    api
      .get(`/admin/user/${encodeURIComponent(username)}/signups`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.status === 403
            ? "This dashboard is admin-only"
            : e?.response?.data?.detail || "Failed to load"
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [username, authLoading]);

  const toggleSelect = (u) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(u)) next.delete(u);
      else next.add(u);
      return next;
    });
  };
  const toggleAll = (rows) => {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.username));
    });
  };
  const sendBulk = async () => {
    if (!selected.size) return;
    if (!bulkMsg.trim() && bulkCoins <= 0) {
      toast.error("Add a message or a coin gift");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/messages/bulk-dm", {
        usernames: Array.from(selected),
        message: bulkMsg,
        coins_amount: Math.max(0, Number(bulkCoins) || 0),
      });
      const { delivered, failed, total_coins_debited } = r.data;
      toast.success(
        `Sent to ${delivered}${failed ? ` · ${failed} failed` : ""}` +
        (total_coins_debited ? ` · ${total_coins_debited.toLocaleString()} coins debited` : "")
      );
      setBulkOpen(false);
      setBulkMsg("");
      setBulkCoins(0);
      setSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk DM failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Backend `require_admin` is the source of truth — a stale client
  // `user.is_admin` (mobile Safari can hold it across nav) should not
  // reject a valid admin whose sidebar shows admin links. Rely on the
  // API response instead: 200 → render, 403 → error branch below.
  //
  // (previously gated on `!user.is_admin` here)

  const totals = data?.totals || {};
  const signups = data?.signups || [];

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div>
          <Link
            to="/search"
            data-testid="admin-signups-back"
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70 mb-2"
          >
            <ArrowLeft className="h-3 w-3" /> search
          </Link>
          <div className="flex items-center gap-3">
            {data?.user?.picture ? (
              <img src={picUrl(data.user.picture)} alt="" className="h-12 w-12 object-cover border-2 border-[#FFC300]/60 flex-shrink-0" />
            ) : (
              <div className="h-12 w-12 bg-[#FFC300]/20 flex items-center justify-center text-[#FFC300] font-bold border-2 border-[#FFC300]/60 flex-shrink-0">
                {data?.user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight truncate">
                {data?.user?.name || `@${username}`}
              </h1>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                admin · referral downline · @{username}
              </p>
              <div className="mt-3">
                <AdminUserTabs username={username} active="signups" />
              </div>
            </div>
            {signups.length > 0 && (
              <button
                type="button"
                data-testid="export-signups-csv"
                onClick={() => {
                  const headers = [
                    "username", "name", "email", "joined_at",
                    "video_count", "follower_count",
                    "boost_earnings_cents", "boost_count",
                    "commission_cents", "commission_count",
                  ];
                  const rows = signups.map((s) => [
                    s.username, s.name || "", s.email || "", s.joined_at || "",
                    s.video_count, s.follower_count,
                    s.boost_earnings_cents, s.boost_count,
                    s.commission_cents, s.commission_count,
                  ]);
                  downloadCsv(`signups-${username}.csv`, headers, rows);
                }}
                title="Download CSV"
                className="ml-auto flex items-center gap-1.5 px-3 py-2 border border-[#FFC300]/60 text-[#FFC300] hover:bg-[#FFC300]/10 font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
              >
                <Download className="h-3.5 w-3.5" /> csv
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-white/40">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : error ? (
          <div className="border border-[#FF007F]/40 bg-[#FF007F]/10 p-4 text-sm text-[#FF007F]">
            {error}
          </div>
        ) : (
          <>
            {/* Totals */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard testid="stat-signups" label="Signups" value={fmtNum(totals.signup_count)} icon={<Users className="h-4 w-4" />} color="#FFC300" />
              <StatCard testid="stat-active" label="Active earners" value={fmtNum(totals.active_earners)} icon={<TrendingUp className="h-4 w-4" />} color="#39FF14" />
              <StatCard testid="stat-commission" label="Commission earned" value={fmtUsd(totals.commission_lifetime_cents)} icon={<Coins className="h-4 w-4" />} color="#FF007F" />
            </div>

            {/* Downline list */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">
                  // downline (tap row → profile · check → bulk)
                </p>
                {signups.length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleAll(signups)}
                    data-testid="bulk-select-all"
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white transition-colors"
                  >
                    {selected.size === signups.length ? (
                      <CheckSquare className="h-3.5 w-3.5 text-[#00FFFF]" />
                    ) : (
                      <Square className="h-3.5 w-3.5" />
                    )}
                    {selected.size === signups.length ? "unselect all" : "select all"}
                  </button>
                )}
              </div>
              {signups.length === 0 ? (
                <div className="border border-white/10 bg-white/[0.02] px-4 py-10 text-center text-white/40 text-sm">
                  No signups attributed to this user yet.
                </div>
              ) : (
                <div className="border border-white/10">
                  {signups.map((s, i) => (
                    <SignupRow
                      key={s.user_id}
                      row={s}
                      rank={i + 1}
                      selected={selected.has(s.username)}
                      onToggle={() => toggleSelect(s.username)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sticky bulk-DM action bar — appears when at least one downline
          user is checked. Opens the compose modal on tap. */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-dm-bar"
          className="fixed bottom-16 left-0 right-0 z-40 border-t border-[#00FFFF]/40 bg-black/90 backdrop-blur-md"
        >
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#00FFFF]">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white"
            >
              clear
            </button>
            <button
              type="button"
              data-testid="bulk-dm-open"
              onClick={() => setBulkOpen(true)}
              className="ml-auto flex items-center gap-2 bg-[#00FFFF] text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all"
            >
              <Send className="h-4 w-4" /> Bulk DM ({selected.size})
            </button>
          </div>
        </div>
      )}

      {/* Compose modal — message + optional coin gift. Coins are debited
          per successful recipient, so we surface the total upfront. */}
      {bulkOpen && (
        <div
          data-testid="bulk-dm-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => !bulkBusy && setBulkOpen(false)}
        >
          <div
            className="w-full max-w-md bg-black border border-[#00FFFF]/50 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#00FFFF]">
                  // bulk.dm
                </p>
                <h2 className="font-display text-xl font-bold">
                  Nudge {selected.size} downline{selected.size === 1 ? "" : "s"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                aria-label="Close"
                disabled={bulkBusy}
                className="text-white/50 hover:text-white disabled:opacity-40"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">
                message
              </label>
              <textarea
                data-testid="bulk-dm-message"
                value={bulkMsg}
                onChange={(e) => setBulkMsg(e.target.value.slice(0, 2000))}
                rows={4}
                placeholder="Keep posting — you're doing amazing 🔥"
                className="w-full bg-black/40 border border-white/10 focus:border-[#00FFFF]/60 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-white/25"
              />
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-1 text-right">
                {bulkMsg.length}/2000
              </p>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1.5">
                <Coins className="h-3 w-3 text-[#FFC300]" />
                coin gift per recipient <span className="text-white/30">(optional)</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="bulk-dm-coins"
                  type="number"
                  min={0}
                  step={10}
                  value={bulkCoins}
                  onChange={(e) => setBulkCoins(Math.max(0, Number(e.target.value) || 0))}
                  className="flex-1 bg-black/40 border border-white/10 focus:border-[#FFC300]/60 focus:outline-none px-3 py-2 text-sm text-white text-right tabular-nums"
                />
                <div className="flex gap-1">
                  {[50, 100, 500, 1000].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setBulkCoins(n)}
                      className="px-2 py-1.5 border border-white/10 hover:border-[#FFC300]/60 font-mono text-[10px] text-white/60 hover:text-[#FFC300]"
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              {bulkCoins > 0 && (
                <p className="mt-2 text-[11px] font-mono text-[#FFC300]">
                  Total debit: {bulkCoins.toLocaleString()} × {selected.size} = <span className="font-bold">{(bulkCoins * selected.size).toLocaleString()}</span> coins
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                disabled={bulkBusy}
                className="flex-1 py-2.5 border border-white/10 text-white/70 hover:text-white hover:border-white/30 font-mono text-[10px] uppercase tracking-widest"
              >
                cancel
              </button>
              <button
                type="button"
                data-testid="bulk-dm-send"
                onClick={sendBulk}
                disabled={bulkBusy || (!bulkMsg.trim() && bulkCoins <= 0)}
                className="flex-[2] flex items-center justify-center gap-2 py-2.5 bg-[#00FFFF] text-black font-bold uppercase tracking-widest text-xs hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {bulkBusy ? "sending" : `send to ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ testid, label, value, icon, color }) {
  return (
    <div data-testid={testid} className="border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">
        <span style={{ color }}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="font-display text-xl font-black leading-none" style={{ color }}>{value}</div>
    </div>
  );
}

function SignupRow({ row, rank, selected, onToggle }) {
  const navigate = useNavigate();
  const [dmBusy, setDmBusy] = useState(false);
  const isTop = rank === 1 && row.commission_cents > 0;

  const openDm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dmBusy) return;
    setDmBusy(true);
    try {
      const r = await api.post("/messages/conversations", { username: row.username });
      const convId = r?.data?.id;
      if (convId) navigate(`/messages/${convId}`);
      else toast.error("Couldn't open the conversation");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open DM");
    } finally {
      setDmBusy(false);
    }
  };

  return (
    <div
      data-testid={`admin-signup-row-${row.username}`}
      className={`flex items-center gap-3 px-3 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors ${
        selected ? "bg-[#00FFFF]/8" : isTop ? "bg-[#FFC300]/5" : ""
      }`}
    >
      {/* Bulk select checkbox — clicking here doesn't navigate, only
          toggles selection so the admin can pile up recipients for the
          bulk DM. */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`bulk-select-${row.username}`}
        aria-label={selected ? `Unselect @${row.username}` : `Select @${row.username}`}
        className="flex-shrink-0 text-white/40 hover:text-[#00FFFF] transition-colors"
      >
        {selected ? (
          <CheckSquare className="h-5 w-5 text-[#00FFFF]" />
        ) : (
          <Square className="h-5 w-5" />
        )}
      </button>
      <div className={`w-7 text-center font-mono text-xs font-bold flex-shrink-0 ${
        rank === 1 ? "text-[#FFC300]" :
        rank === 2 ? "text-white/80" :
        rank === 3 ? "text-[#39FF14]" : "text-white/30"
      }`}>
        {rank === 1 ? <Crown className="h-4 w-4 mx-auto" /> : rank}
      </div>
      <Link
        to={`/u/${row.username}`}
        data-testid={`admin-signup-profile-${row.username}`}
        className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-90"
      >
        {row.picture ? (
          <img src={picUrl(row.picture)} alt="" className="h-11 w-11 object-cover border border-white/10 flex-shrink-0" />
        ) : (
          <div className="h-11 w-11 bg-[#FFC300]/20 flex items-center justify-center font-bold text-[#FFC300] border border-white/10 flex-shrink-0">
            {row.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold truncate">{row.name || `@${row.username}`}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 truncate">
            @{row.username} · joined {fmtDate(row.joined_at)}
          </p>
          {row.email && (
            <p className="font-mono text-[10px] text-white/30 truncate flex items-center gap-1 mt-0.5">
              <Mail className="h-2.5 w-2.5" /> {row.email}
            </p>
          )}
          <div className="flex items-center gap-3 font-mono text-[10px] mt-1">
            <span className="text-white/50">
              <span className="text-white">{fmtNum(row.video_count)}</span> videos
            </span>
            <span className="text-white/50">
              <span className="text-white">{fmtNum(row.follower_count)}</span> followers
            </span>
            {row.commission_cents > 0 && (
              <span className="text-[#39FF14]">
                +{fmtUsd(row.commission_cents)} paid to referrer
              </span>
            )}
          </div>
        </div>
      </Link>
      {/* One-tap DM shortcut — creates or opens the conversation with
          this downline user (POST /messages/conversations) and navigates
          straight into the thread. stopPropagation so it doesn't also
          trigger the profile Link. */}
      <button
        type="button"
        onClick={openDm}
        disabled={dmBusy}
        data-testid={`admin-signup-dm-${row.username}`}
        title={`Send @${row.username} a message`}
        aria-label={`Send @${row.username} a message`}
        className="h-9 w-9 flex items-center justify-center border border-[#00FFFF]/40 text-[#00FFFF] hover:bg-[#00FFFF]/10 disabled:opacity-40 flex-shrink-0"
      >
        {dmBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" strokeWidth={1.7} />}
      </button>
      <ChevronRight className="h-5 w-5 text-white/30 flex-shrink-0" />
    </div>
  );
}
